import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowForward as NextIcon,
  Sports as SportsIcon,
  Tag as TagIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Player, playerService } from '@/services/playerService';
import { PlayerProfilePhotoManager } from './PlayerProfilePhotoManager';
import { PlayerAvatar } from './PlayerAvatar';
import { authManager } from '@/services/authManager';
import { PLAYER_COLORS, getRandomAvailableColor } from '@/utils/playerColors';

interface PlayerInformationTabProps {
  player?: Player;
  onUpdate?: () => void;
  canModify: boolean;
  onHasChangesChange?: (hasChanges: boolean) => void;
  wizardMode?: boolean;
  onNext?: (playerData: any) => void;
  onCancel?: () => void;
  initialData?: any;
  onDataChange?: (data: any) => void;
  onPhotoChange?: (file: File | null) => void;
  onPhotoTypeChange?: (type: 'UPLOAD' | 'GRAVATAR') => void;
  initialPhotoType?: 'UPLOAD' | 'GRAVATAR';
  onPhotoPreviewChange?: (url: string | null) => void;
  initialPhotoPreview?: string | null;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  jerseyNumber: string;
  homeAddressStreet: string;
  homeAddressCity: string;
  homeAddressState: string;
  homeAddressZip: string;
  playerColor: string;
}

const POSITIONS = [
  'Goalkeeper', 
  'Defender', 
  'Midfielder', 
  'Forward',
  'Center Back', 
  'Full Back', 
  'Wing Back', 
  'Defensive Midfielder',
  'Central Midfielder', 
  'Attacking Midfielder', 
  'Winger', 
  'Striker'
];

const PlayerInformationTab: React.FC<PlayerInformationTabProps> = ({
  player,
  onUpdate,
  canModify,
  onHasChangesChange,
  wizardMode = false,
  onNext,
  onCancel,
  initialData,
  onDataChange,
  onPhotoChange,
  onPhotoTypeChange,
  initialPhotoType,
  onPhotoPreviewChange,
  initialPhotoPreview
}) => {
  const navigate = useNavigate();

  // Fetch all players to determine available colors for auto-assign
  const { data: allPlayers } = useQuery(
    'players',
    () => playerService.getPlayers(),
    { staleTime: 1000 * 60 * 5 }
  );

  const [formData, setFormData] = useState<FormState>(() => {
    if (player) {
      const nameParts = player.name.split(' ');
      return {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: player.email || '',
        position: player.position || '',
        jerseyNumber: player.jerseyNumber?.toString() || '',
        homeAddressStreet: player.homeAddressStreet || '',
        homeAddressCity: player.homeAddressCity || '',
        homeAddressState: player.homeAddressState || '',
        homeAddressZip: player.homeAddressZip || '',
        playerColor: player.playerColor || ''
      };
    } else if (wizardMode && initialData) {
      return initialData;
    } else {
      return {
        firstName: '',
        lastName: '',
        email: '',
        position: '',
        jerseyNumber: '',
        homeAddressStreet: '',
        homeAddressCity: '',
        homeAddressState: '',
        homeAddressZip: '',
        playerColor: ''
      };
    }
  });

  const [originalData, setOriginalData] = useState<FormState>(formData);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  // Only update form data when switching between different players or when initialData changes significantly
  useEffect(() => {
    if (player) {
      const nameParts = player.name.split(' ');
      const data = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: player.email || '',
        position: player.position || '',
        jerseyNumber: player.jerseyNumber?.toString() || '',
        homeAddressStreet: player.homeAddressStreet || '',
        homeAddressCity: player.homeAddressCity || '',
        homeAddressState: player.homeAddressState || '',
        homeAddressZip: player.homeAddressZip || '',
        playerColor: player.playerColor || ''
      };
      setFormData(data);
      setOriginalData(data);
    } else if (wizardMode && initialData && JSON.stringify(formData) !== JSON.stringify(initialData)) {
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [player?.id, wizardMode]); // Only depend on player ID and wizard mode, not initialData

  // Auto-assign a random color for new players once allPlayers loads
  useEffect(() => {
    if (wizardMode && allPlayers) {
      setFormData(prev => {
        if (prev.playerColor) return prev;
        return { ...prev, playerColor: getRandomAvailableColor(allPlayers) };
      });
    } else if (!wizardMode && player && allPlayers && !player.playerColor) {
      // Existing player without a stored color — assign one
      setFormData(prev => {
        if (prev.playerColor) return prev;
        return { ...prev, playerColor: getRandomAvailableColor(allPlayers) };
      });
    }
  }, [wizardMode, allPlayers, player?.id]);

  // Detect changes and notify parent
  useEffect(() => {
    const hasChangesValue = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(hasChangesValue);
    onHasChangesChange?.(hasChangesValue);
    
    // In wizard mode, save form data to parent
    if (wizardMode && onDataChange) {
      onDataChange(formData);
    }
  }, [formData, originalData, onHasChangesChange, wizardMode, onDataChange]);

  const handleInputChange = (field: keyof FormState, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    // Validate required fields with safety checks
    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    const finalData = formData;

    // In wizard mode, just pass the data to parent - don't create player yet
    if (wizardMode) {
      const playerData = {
        firstName: finalData.firstName,
        lastName: finalData.lastName,
        email: finalData.email || null,
        position: finalData.position || null,
        jerseyNumber: finalData.jerseyNumber ? parseInt(finalData.jerseyNumber) : null,
        homeAddressStreet: finalData.homeAddressStreet || null,
        homeAddressCity: finalData.homeAddressCity || null,
        homeAddressState: finalData.homeAddressState || null,
        homeAddressZip: finalData.homeAddressZip || null,
        playerColor: finalData.playerColor || null,
        photoType: 'UPLOAD' // Will be updated by photo type changes
      };
      onNext?.(playerData);
      return;
    }

    // Regular mode - create player immediately (existing functionality)
    try {
      setLoading(true);

      const response = await playerService.createPlayer({
        name: `${finalData.firstName} ${finalData.lastName}`.trim(),
        position: finalData.position || null,
        jerseyNumber: finalData.jerseyNumber ? parseInt(finalData.jerseyNumber) : null,
        homeAddressStreet: finalData.homeAddressStreet || null,
        homeAddressCity: finalData.homeAddressCity || null,
        homeAddressState: finalData.homeAddressState || null,
        homeAddressZip: finalData.homeAddressZip || null,
        playerColor: finalData.playerColor || null
      });

      toast.success('Player created! Now add teams or click Done to finish.');
      onNext?.(response);
    } catch (err: any) {
      toast.error('Failed to create player');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!player) {
      toast.error('No player to update');
      return;
    }

    // If no changes, just navigate back (Done button behavior)
    if (!hasChanges) {
      navigate('/players');
      return;
    }

    // Validate required fields with safety checks
    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    const finalData = formData;

    try {
      setLoading(true);

      await playerService.updatePlayer(player.id, {
        name: `${finalData.firstName} ${finalData.lastName}`.trim(),
        email: finalData.email || null,
        position: finalData.position || null,
        jerseyNumber: finalData.jerseyNumber ? parseInt(finalData.jerseyNumber) : null,
        homeAddressStreet: finalData.homeAddressStreet || null,
        homeAddressCity: finalData.homeAddressCity || null,
        homeAddressState: finalData.homeAddressState || null,
        homeAddressZip: finalData.homeAddressZip || null,
        playerColor: finalData.playerColor || null
      });

      toast.success('Player updated successfully');
      setOriginalData(finalData);
      setHasChanges(false);
      
      // If email was cleared and player was using Gravatar, switch to upload mode
      if (!finalData.email?.trim() && player.email?.trim()) {
        try {
          const photoToken = await authManager.getAccessToken();
          await fetch(`/api/v1/players/${player.id}/profile-photo/upload-mode`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${photoToken}`
            }
          });
          // Trigger both photo update and config reload
          window.dispatchEvent(new Event('playerPhotoUpdated'));
          window.dispatchEvent(new Event('playerPhotoConfigChanged'));
        } catch (err) {
          console.error('Failed to switch photo mode:', err);
        }
      }
      
      onUpdate?.();
      navigate('/players');
    } catch (err: any) {
      toast.error('Failed to update player');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (wizardMode && onCancel) {
      onCancel();
    } else {
      // For edit mode, discard changes and return to players screen
      setFormData(originalData);
      setHasChanges(false);
      // Navigate back to players page
      navigate('/players');
    }
  };

  return (
      <Box>
        {/* Mobile heading */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: { xs: 'block', md: 'none' } }}>
          {wizardMode ? 'New Player' : player?.name ? `${player.name}'s Profile` : 'Player Profile'}
        </Typography>
        {/* Desktop heading */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
          {wizardMode ? 'New Player' : 'Player Profile'}
        </Typography>

        {/* Profile Photo Card - Available in wizard mode */}
        {canModify && (
          <Card sx={{ mb: 3, mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile Photo
              </Typography>
              {wizardMode ? (
                <PlayerProfilePhotoManager
                  playerId="wizard-temp"
                  playerEmail={formData.email}
                  wizardMode={true}
                  onPhotoChange={onPhotoChange}
                  onPhotoTypeChange={onPhotoTypeChange}
                  initialPhotoType={initialPhotoType}
                  onPhotoPreviewChange={onPhotoPreviewChange}
                  initialPhotoPreview={initialPhotoPreview}
                />
              ) : player ? (
                <PlayerProfilePhotoManager
                  playerId={player.id}
                  playerEmail={formData.email}
                />
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Basic Information Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  fullWidth
                  required
                  disabled={!canModify}
                  error={!formData.firstName.trim() && formData.firstName !== ''}
                  helperText={!formData.firstName.trim() && formData.firstName !== '' ? 'First name is required' : ''}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  fullWidth
                  required
                  disabled={!canModify}
                  error={!formData.lastName.trim() && formData.lastName !== ''}
                  helperText={!formData.lastName.trim() && formData.lastName !== '' ? 'Last name is required' : ''}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  fullWidth
                  disabled={!canModify}
                  helperText="Optional - used for Gravatar profile photos"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={POSITIONS}
                  value={formData.position || null}
                  onChange={(_, value) => handleInputChange('position', value || '')}
                  disabled={!canModify}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Position" 
                      placeholder="Select or type position"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SportsIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  )}
                  freeSolo
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Jersey Number"
                  value={formData.jerseyNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 99)) {
                      handleInputChange('jerseyNumber', value);
                    }
                  }}
                  disabled={!canModify}
                  inputProps={{ 
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: { textAlign: 'right' }
                  }}
                  placeholder="e.g. 10"
                  fullWidth
                  InputProps={{
                    startAdornment: <TagIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Highlight Color — appears on calendars and maps
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {PLAYER_COLORS.map((color) => {
                    const isSelected = formData.playerColor === color;
                    const othersWithColor = allPlayers?.filter(
                      p => p.playerColor === color && p.id !== player?.id
                    ) || [];
                    const tooltipText = othersWithColor.length > 0
                      ? othersWithColor.map(p => p.name).join(', ')
                      : '';
                    return (
                      <Tooltip
                        key={color}
                        title={tooltipText}
                        disableHoverListener={othersWithColor.length === 0}
                      >
                        <Box
                          onClick={() => canModify && handleInputChange('playerColor', color)}
                          sx={{
                            position: 'relative',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: color,
                            cursor: canModify ? 'pointer' : 'default',
                            boxShadow: isSelected ? `0 0 0 2px white, 0 0 0 4px currentColor` : 'none',
                            transition: 'transform 0.1s',
                            '&:hover': canModify ? { transform: 'scale(1.15)' } : {},
                          }}
                        >
                          {othersWithColor.length === 1 && (
                            <PlayerAvatar
                              playerId={othersWithColor[0].id}
                              player={othersWithColor[0]}
                              size="small"
                              sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -4,
                                width: 14,
                                height: 14,
                                fontSize: '0.45rem',
                                border: '1.5px solid white',
                              }}
                            />
                          )}
                          {othersWithColor.length >= 2 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -4,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                border: '1.5px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography sx={{ fontSize: '0.4rem', color: 'white', lineHeight: 1, fontWeight: 700 }}>
                                {othersWithColor.length}+
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canModify && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {wizardMode ? (
              <>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={loading}
                  startIcon={<CancelIcon />}
                  aria-label="Cancel player creation"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  startIcon={<NextIcon />}
                  aria-label="Create player and continue to teams"
                >
                  {loading ? 'Creating...' : (wizardMode ? 'Next' : 'Create')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  startIcon={<CancelIcon />}
                  aria-label="Cancel changes and return to players page"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                  startIcon={hasChanges ? <SaveIcon /> : <CheckIcon />}
                  aria-label={hasChanges ? "Save player information changes" : "Return to players page"}
                >
                  {loading ? 'Saving...' : hasChanges ? 'Save' : 'Done'}
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
  );
};

export default PlayerInformationTab;
